/**
 * Shape of one connectable provider row — the join of the integration catalog
 * (display metadata), the tenant's admin-enabled integrations, and the calling
 * user's own OAuth connections.
 *
 * This type lives in `libs/moo` rather than `apps/console` on purpose. The
 * composer chip that consumes it renders inside `ChatPanel`, which is part of
 * this library, and `libs/moo` is a buildable Nx lib that cannot import from
 * `apps/console` (`@nx/enforce-module-boundaries` + the rollup build both
 * reject it). So the type flows lib -> app: the console's React Query hooks
 * import it from `@cowbell/moo` and produce values of this shape, and the
 * library's presentational components consume it via `ConnectedAppsProvider`.
 */
export interface ConnectableProvider {
  /** Backend provider key, e.g. `google_calendar`, `slack`. */
  readonly provider: string;
  /** Human-readable name from the catalog, e.g. "Google Calendar". */
  readonly displayName: string;
  /** Catalog-supplied logo URL. May be empty — callers fall back to a local asset. */
  readonly logoUrl: string;
  /** Whether the CURRENT USER has connected this provider. */
  readonly connected: boolean;
  /** Connection status when connected, otherwise null. */
  readonly status: UserConnectionStatus | null;
  /** The connected account label (e.g. an email address), otherwise null. */
  readonly externalAccountLabel: string | null;
  /**
   * The caller's stored on/off preference for this app.
   *
   * `null` means the backend does not report the field yet, and the UI hides
   * the mute control entirely rather than showing a switch that cannot work.
   *
   * Deliberately independent of `status`: a preference is not a health signal.
   * A token expiring must not overwrite a choice the user made, or reconnecting
   * would silently leave the app off with nothing explaining why. Use
   * `isProviderUsable` to decide what to *show*.
   */
  readonly enabled: boolean | null;
}

export type UserConnectionStatus = 'active' | 'revoked' | 'expired' | 'error';

/** Statuses where the stored grant no longer works and OAuth must be re-run. */
export const BROKEN_CONNECTION_STATUSES: readonly UserConnectionStatus[] = [
  'revoked',
  'expired',
  'error',
];

export function isConnectionBroken(provider: ConnectableProvider): boolean {
  return (
    provider.connected &&
    BROKEN_CONNECTION_STATUSES.includes(provider.status as UserConnectionStatus)
  );
}

/**
 * Whether an agent can actually use this app right now — connected, healthy,
 * and not muted by the caller.
 *
 * This is what the switch reflects, so a broken connection reads as off even
 * though the underlying preference is untouched.
 */
export function isProviderUsable(provider: ConnectableProvider): boolean {
  if (!provider.connected || isConnectionBroken(provider)) return false;
  // `null` = backend has no mute support yet, so a healthy connection is usable.
  return provider.enabled !== false;
}
